import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Lock, Send, MessageCircle } from "lucide-react";
import { PageHeader, StatusBadge, fmtDateTime, Empty } from "../../components/admin/ui";

const STATUSES = ["new", "in_prayer", "answered", "follow_up", "closed"];

const ReplyForm = ({ pr, onSent }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/prayer-requests/${pr.id}/reply`, { message: msg });
      toast.success(pr.email ? `Reply sent to ${pr.email}.` : "Reply saved.");
      setMsg("");
      onSent && onSent();
    } catch { toast.error("Reply failed."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="mt-4 border-t border-[#C9A44C]/15 pt-4 space-y-2" data-testid={`reply-form-${pr.id}`}>
      <label className="field-label">Respond as leadership {pr.email && <span className="opacity-60 normal-case tracking-normal">— will email {pr.email}</span>}</label>
      <textarea rows={3} className="input-fire" placeholder="Standing with you in prayer…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid={`reply-text-${pr.id}`} />
      <button type="submit" disabled={busy || !msg.trim()} className="btn-fire !py-2.5 !px-5 !text-sm" data-testid={`reply-send-${pr.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        {busy ? "Sending…" : "Send Reply"}
      </button>
    </form>
  );
};

const PrayerRequests = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/prayer-requests"); setItems(r.data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/prayer-requests/${id}/status`, { status, follow_up_needed: status === "follow_up" });
      toast.success("Updated."); load();
    } catch { toast.error("Update failed."); }
  };

  return (
    <div className="space-y-8" data-testid="admin-prayer-requests">
      <PageHeader
        overline="Intercession"
        title={<><span className="italic gold-text">Prayer</span> requests</>}
        subtitle="Reply personally, update status, and assign (assignments coming soon). Never displayed publicly."
      />

      <div className="bento-card !p-5 border-[#F28C18]/30 flex items-center gap-3" data-testid="livechat-coming-soon">
        <span className="glow-blob" />
        <MessageCircle className="h-5 w-5 text-[#F28C18]" />
        <div className="text-sm text-[#F6F0E6]">
          <span className="font-medium">Live Chat — Coming Soon.</span>{" "}
          <span className="text-[#D9D9D9]/75">Real-time prayer chat with covenant leaders. Until then, replies below send a personal email to the requester.</span>
        </div>
      </div>

      {loading ? (
        <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <Empty title="No prayer requests yet" subtitle="When a visitor or member submits the form, it appears here privately." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {items.map((p) => (
            <article key={p.id} className="bento-card !p-6" data-testid={`pr-${p.id}`}>
              <span className="glow-blob" />
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{fmtDateTime(p.created_at)}</div>
                  <h3 className="font-serif-display text-xl text-[#F6F0E6] mt-1">{p.name || "Anonymous"}</h3>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[#D9D9D9]/75">
                    {p.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3 text-[#C9A44C]" /> {p.email}</span>}
                    {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-[#C9A44C]" /> {p.phone}</span>}
                    {p.private && <span className="inline-flex items-center gap-1 text-[#F28C18]"><Lock className="h-3 w-3" /> Private</span>}
                  </div>
                </div>
                <StatusBadge status={p.status || "new"} />
              </div>

              <p className="mt-4 text-sm text-[#F6F0E6] leading-relaxed whitespace-pre-wrap">{p.request}</p>

              {Array.isArray(p.replies) && p.replies.length > 0 && (
                <div className="mt-4 space-y-2">
                  {p.replies.map((rep) => (
                    <div key={rep.id} className="pl-3 border-l-2 border-[#C9A44C]/40">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-[#C9A44C]">{rep.by_name} · {fmtDateTime(rep.created_at)}</div>
                      <div className="text-sm text-[#D9D9D9] mt-1 whitespace-pre-wrap">{rep.message}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatus(p.id, s)} className={`text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full border ${p.status === s ? "border-[#C9A44C] bg-[#C9A44C]/10 text-[#C9A44C]" : "border-[#C9A44C]/25 text-[#D9D9D9] hover:border-[#C9A44C]/55"}`} data-testid={`pr-${p.id}-status-${s}`}>
                    {s.replace(/_/g, " ")}
                  </button>
                ))}
              </div>

              <ReplyForm pr={p} onSent={load} />
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrayerRequests;
